import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Folder } from 'lucide-react';
import { emailService } from '../../services';
import { messageHandler } from '../../services/message-handler.service';
import type { EmailFolder } from '../../types/email';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  EmptyState,
  Modal,
  Form,
  FormField,
  FormLabel,
  FormInput,
  FormTextarea,
  Badge,
} from '../ui';

function FolderManager() {
  const [modal, setModal] = useState<{ type: 'create' | 'edit' | ''; data?: EmailFolder }>({
    type: '',
  });
  const queryClient = useQueryClient();

  const { data: folders } = useQuery({
    queryKey: ['email-folders'],
    queryFn: () => emailService.getFolders(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<EmailFolder>) => emailService.createFolder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-folders'] });
      messageHandler.success('Pasta criada com sucesso');
      setModal({ type: '' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EmailFolder> }) =>
      emailService.updateFolder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-folders'] });
      messageHandler.success('Pasta atualizada com sucesso');
      setModal({ type: '' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => emailService.deleteFolder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-folders'] });
      messageHandler.success('Pasta excluída com sucesso');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      name: formData.get('name') as string,
      slug: formData.get('name')?.toString().toLowerCase().replace(/\s+/g, '-') || '',
      description: formData.get('description') as string,
    };

    if (modal.type === 'edit' && modal.data) {
      updateMutation.mutate({ id: modal.data.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (folder: EmailFolder) => {
    if (folder.is_system) {
      messageHandler.error('Não é possível excluir pastas do sistema');
      return;
    }

    messageHandler.confirm(
      'Excluir Pasta',
      'Tem certeza que deseja excluir esta pasta? Todos os emails serão movidos para a Caixa de Entrada.',
      () => deleteMutation.mutate(folder.id)
    );
  };

  return (
    <>
      <div className="mb-4">
        <Button onClick={() => setModal({ type: 'create' })}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Pasta
        </Button>
      </div>

      {folders?.length > 0 ? (
        <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="relative">
                  <span className="sr-only">Ações</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {folders.map(folder => (
                <TableRow key={folder.id}>
                  <TableCell>{folder.name}</TableCell>
                  <TableCell>{folder.description || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={folder.is_system ? 'info' : 'secondary'}>
                      {folder.is_system ? 'Sistema' : 'Personalizada'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      {!folder.is_system && (
                        <>
                          <Button
                            variant="ghost"
                            onClick={() => setModal({ type: 'edit', data: folder })}
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => handleDelete(folder)}
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          icon={<Folder className="h-12 w-12" />}
          title="Nenhuma pasta personalizada"
          description="Crie pastas personalizadas para organizar seus emails"
          action={{
            label: 'Criar Pasta',
            onClick: () => setModal({ type: 'create' }),
          }}
        />
      )}

      <Modal
        isOpen={modal.type !== ''}
        onClose={() => setModal({ type: '' })}
        title={modal.type === 'create' ? 'Nova Pasta' : 'Editar Pasta'}
        size="md"
      >
        <Form onSubmit={handleSubmit}>
          <FormField>
            <FormLabel htmlFor="name" required>
              Nome
            </FormLabel>
            <FormInput id="name" name="name" defaultValue={modal.data?.name} required />
          </FormField>

          <FormField>
            <FormLabel htmlFor="description">Descrição</FormLabel>
            <FormTextarea
              id="description"
              name="description"
              defaultValue={modal.data?.description}
              rows={3}
            />
          </FormField>

          <div className="mt-6 flex justify-end space-x-2">
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
              {modal.type === 'create' ? 'Criar' : 'Salvar'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setModal({ type: '' })}>
              Cancelar
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

export default FolderManager;
